import React, { useCallback, useEffect, useMemo, useState } from "react";
import { GeoJson } from "pigeon-maps";
import {
  GEOJSON_URL,
  loadSanitizedGeoJson,
} from "../../utils/geojson-cache";

type LatLng = [number, number];

type RegionShapesOverlayProps = {
  zoom: number;
  countrySlug: string;
  hoveredRegionSlug: string | null;
  activeRegionSlug: string | null;
  onRegionClick: (region: {
    countrySlug: string;
    regionSlug: string;
    name: string;
    lat: number;
    lng: number;
  }) => void;
  onRegionEnter: (region: {
    countrySlug: string;
    regionSlug: string;
    name: string;
    lat: number;
    lng: number;
  }) => void;
  onRegionLeave: () => void;
  mapState?: {
    width: number;
    height: number;
    center: LatLng;
    zoom: number;
    bounds?: unknown;
  };
  latLngToPixel?: (latLng: LatLng, center?: LatLng, zoom?: number) => [number, number];
  pixelToLatLng?: (pixel: [number, number]) => LatLng;
};

/**
 * Admin-1 region outlines for the active country.
 * Hover: thin white dotted. Selected: white stroke. Others: thin green.
 */
const RegionShapesOverlay: React.FC<RegionShapesOverlayProps> = ({
  zoom,
  countrySlug,
  hoveredRegionSlug,
  activeRegionSlug,
  onRegionClick,
  onRegionEnter,
  onRegionLeave,
  mapState,
  latLngToPixel,
  pixelToLatLng,
}) => {
  const [allData, setAllData] = useState<any>(null);

  useEffect(() => {
    if (!countrySlug) return;
    let cancelled = false;
    const load = () => {
      loadSanitizedGeoJson(GEOJSON_URL.regions).then((json) => {
        if (!cancelled) setAllData(json);
      });
    };
    // Defer heavy regions parse so country page content paints first
    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(load, { timeout: 900 });
    } else {
      timeoutId = setTimeout(load, 50);
    }
    return () => {
      cancelled = true;
      if (idleId != null && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, [countrySlug]);

  const data = useMemo(() => {
    if (!allData?.features || !countrySlug) return null;
    const features = allData.features.filter(
      (f: any) => f?.properties?.countrySlug === countrySlug
    );
    if (features.length === 0) return null;
    return { type: "FeatureCollection", features };
  }, [allData, countrySlug]);

  const hoverData = useMemo(() => {
    if (!data || !hoveredRegionSlug || hoveredRegionSlug === activeRegionSlug) {
      return null;
    }
    const feature = data.features.find(
      (f: any) => f?.properties?.regionSlug === hoveredRegionSlug
    );
    if (!feature) return null;
    return { type: "FeatureCollection", features: [feature] };
  }, [data, hoveredRegionSlug, activeRegionSlug]);

  const styleCallback = useCallback(
    (feature: any) => {
      const slug = feature?.properties?.regionSlug as string | undefined;
      const isActive = Boolean(slug && slug === activeRegionSlug);

      if (isActive) {
        const strokeW =
          zoom <= 5 ? 3.25 : zoom <= 7 ? 2.75 : zoom <= 10 ? 2.35 : 2;
        return {
          fill: "rgba(0, 0, 0, 0)",
          stroke: "#ffffff",
          strokeWidth: String(strokeW),
          strokeLinejoin: "round",
          strokeLinecap: "round",
          paintOrder: "stroke fill",
          pointerEvents: "none",
          r: "0",
        };
      }

      // Thin region lines inside the country
      return {
        fill: "rgba(0, 83, 25, 0.04)",
        stroke: "rgba(0, 83, 25, 0.55)",
        strokeWidth: zoom <= 5 ? "1.1" : "1.35",
        strokeLinejoin: "round",
        strokeLinecap: "round",
        pointerEvents: zoom <= 7 ? "stroke" : "none",
        r: "0",
      };
    },
    [activeRegionSlug, zoom]
  );

  const hoverStyleCallback = useCallback(() => {
    const strokeW = zoom <= 5 ? 2 : zoom <= 8 ? 1.75 : 1.5;
    return {
      fill: "rgba(0, 0, 0, 0)",
      stroke: "#ffffff",
      strokeWidth: String(strokeW),
      strokeDasharray: "3.5 3",
      strokeLinejoin: "round",
      strokeLinecap: "round",
      paintOrder: "stroke fill",
      pointerEvents: "none",
      r: "0",
    };
  }, [zoom]);

  if (!data || !mapState?.width || !mapState?.height || !latLngToPixel) {
    return null;
  }
  if (zoom >= 9) return null;

  const mapProps = {
    mapState,
    latLngToPixel,
    pixelToLatLng,
  };

  const payloadRegion = (props: any) => ({
    countrySlug: props.countrySlug as string,
    regionSlug: props.regionSlug as string,
    name: props.name as string,
    lat: Number(props.lat) || 0,
    lng: Number(props.lng) || 0,
  });

  return (
    <>
      <GeoJson
        {...mapProps}
        data={data}
        hover={false}
        styleCallback={styleCallback}
        onClick={({ payload }) => {
          const props = payload?.properties;
          if (!props?.regionSlug) return;
          onRegionClick(payloadRegion(props));
        }}
        onMouseOver={({ payload }) => {
          const props = payload?.properties;
          if (!props?.regionSlug) return;
          onRegionEnter(payloadRegion(props));
        }}
        onMouseOut={({ event }) => {
          const e = event as unknown as React.MouseEvent<SVGGElement>;
          const related = e.relatedTarget as Node | null;
          const current = e.currentTarget as Node | null;
          if (related && current && current.contains(related)) return;
          onRegionLeave();
        }}
      />
      {hoverData && (
        <GeoJson
          {...mapProps}
          data={hoverData}
          hover={false}
          styleCallback={hoverStyleCallback}
        />
      )}
    </>
  );
};

export default RegionShapesOverlay;
